import { Component } from 'react';
import Joi from 'joi';
import logService from '../../services/wss/logService3';
import CoreComponent from '../../services/engines/CoreComponent';

/**
 * JxFormValidationBase — Joi-schema validation only. No drag, no resize, no rendering opinions.
 * extend this, set `this.schema` in the constructor (plain object of Joi field schemas,
 * e.g. { postcode: Joi.string().label('Postcode') }), and you get:
 *   - this.state.inputs / this.state.errors
 *   - this.handleChange(name, value) — validates the single field, updates inputs+errors
 *   - this.validate() — validates everything, returns null or an errors object
 *   - this.labelFor(name) — reads .label() off the Joi schema via the public describe() API
 *
 * Uses modern `joi` (NOT the deprecated joi-browser) — schema.validate(value) API.
 */
export default class JxFormValidationBase extends CoreComponent {
    schema = {};

    state = {
        inputs: {},
        errors: {},
    };

    constructor(props) {
        super(props);
        this.name = 'JxFormValidationBase'; // required by JxForm
    }

    componentDidMount() {
        // super.componentDidMount();

        const { id } = this.props;
        if (id === undefined) {
            this.id = `${this.name}_01`;

            this.logDebug('', `id not set, using default: ${this.id}`);
        } else {
            this.id = id;
        }
    }

    componentWillUnmount() {
        //   super.componentWillUnmount();
    }

    /**
     * Returns the window id — uses the id prop directly if set (assigned by JxWindowManager),
     * falls back to name_01 for dialogs without a manager-assigned id.
     * @returns {string}
     */
    getId() {
        // props.id covers the first render, this.id is only set in componentDidMount
        return this.id ?? this.props.id ?? `${this.name}_01`;
    }

    /**
     *
     * @param {string} method the method name that had an error
     * @param {*} message the custom error message
     */
    logError(method, message) {
        logService.logError(`${this.getId()}.${method}: ${message}`);
    }

    logDebug(method, message) {
        logService.log(`${this.getId()}.${method}: ${message}`);
    }

    /**
     * Validates a single field against this.schema, updates inputs + errors in one setState.
     * @param {string} name
     * @param {*} value
     */
    handleChange = (name, value) => {
        const fieldSchema = Joi.object({ [name]: this.schema[name] });
        const { error } = fieldSchema.validate({ [name]: value });
        const message = error ? error.details[0].message : undefined;

        this.setState((s) => ({
            inputs: { ...s.inputs, [name]: value },
            errors: { ...s.errors, [name]: message },
        }));
    };

    /**
     *
     * @param {number} mode -1 for cancel 0 for save
     * @returns nothing, stays open when fields are not matching Joi rules
     */
    handleClose = (mode) => {
        const onClose = this.props.onClose;

        if (mode >= 0) {
            const errors = this.validate();
            if (errors !== null) {
                this.setState({ errors });
                return;
            }
        }

        onClose(mode, this.state.inputs);
    };

    /**
     * Validates the full this.state.inputs against this.schema.
     * @returns {object|null} errors object (field -> message), or null if valid
     */
    validate() {
        try {
            const fullSchema = Joi.object(this.schema);
            const { error } = fullSchema.validate(this.state.inputs, { abortEarly: false });
            if (!error) return null;

            const errors = {};
            for (const item of error.details) errors[item.path[0]] = item.message;
            return errors;
        } catch (error) {
            this.logDebug('validate', `ex: ${error.message}`);
        }
    }

    /**
     * Reads the .label() set on a Joi schema field, via Joi's public describe() API.
     * @param {string} name
     */
    labelFor = (name) => this.schema[name]?.describe()?.flags?.label ?? name;
}
